/**
 * VH Staff Branded Login Page
 *
 * Restaurant-specific login page with custom branding.
 * URL format: /staff/:slug (e.g., /staff/the-flavor-lab)
 *
 * Staff login with email and password.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, CheckCircle, AlertCircle, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface Restaurant {
  restaurant_id: number;
  restaurant_name: string;
  slug: string;
  rest_account_id: number;
  logo_url: string | null;
}

const VHStaffLoginBranded = () => {
  const { slug } = useParams<{ slug: string }>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [restaurantLoading, setRestaurantLoading] = useState(true);
  const [restaurantError, setRestaurantError] = useState('');

  // Fetch restaurant info by slug
  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!slug) {
        setRestaurantError('Invalid restaurant URL');
        setRestaurantLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/vh-staff/auth/restaurant/${slug}`);
        setRestaurant(response.data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setRestaurantError('Restaurant not found');
        } else {
          setRestaurantError('Failed to load restaurant info');
        }
      } finally {
        setRestaurantLoading(false);
      }
    };

    fetchRestaurant();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/vh-staff/auth/login', {
        email,
        password,
        remember_me: rememberMe
      });

      if (response.data.success) {
        // Clear any demo flags to prevent auth conflicts
        // VH Staff and Demo use separate auth systems
        localStorage.removeItem('isDemo');
        localStorage.removeItem('demoPlan');

        // Store user info for VH Staff Dashboard (cookie is httpOnly, can't be read by JS)
        localStorage.setItem('user_type', 'vh_staff');
        localStorage.setItem('staff_name', response.data.name || 'Staff');
        localStorage.setItem('restaurant_name', response.data.active_restaurant?.name || restaurant?.restaurant_name || 'Restaurant');
        localStorage.setItem('restaurant_slug', slug || '');
        localStorage.setItem('restaurant_logo_url', restaurant?.logo_url || '');
        localStorage.setItem('vh_staff_data', JSON.stringify({
          staff_id: response.data.staff_id,
          email: response.data.email,
          name: response.data.name,
          restaurants: response.data.restaurants,
          active_restaurant: response.data.active_restaurant
        }));

        // Redirect to dedicated VH Staff Dashboard (not RestaurantDashboard)
        window.location.replace('/vh-staff-dashboard');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Invalid email or password');
      } else if (err.response?.status === 403) {
        setError(err.response?.data?.detail || 'Your account has been deactivated');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state for restaurant info
  if (restaurantLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#FDB913] animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Restaurant not found
  if (restaurantError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Restaurant Not Found</h1>
          <p className="text-gray-500 mb-6">{restaurantError}</p>
          <Link
            to="/vh-staff-login"
            className="block w-full py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] transition-colors"
          >
            Go to Staff Login
          </Link>
        </motion.div>
      </div>
    );
  }

  // Main login form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full"
      >
        {/* Header with Restaurant Branding */}
        <div className="text-center mb-8">
          {restaurant?.logo_url ? (
            <img
              src={restaurant.logo_url}
              alt={restaurant.restaurant_name}
              className="h-20 w-auto max-w-[280px] object-contain mx-auto mb-4"
            />
          ) : (
            <div className="h-16 w-16 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-[#FDB913] font-bold text-xl">AI</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {restaurant?.restaurant_name || 'Staff Login'}
          </h1>
          <p className="text-gray-500 mt-2">
            Sign in to access the dashboard
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Remember this device + Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-[#FDB913] border-gray-300 rounded focus:ring-[#FDB913] cursor-pointer"
              />
              <label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer select-none">
                Remember this device
              </label>
            </div>
            <Link
              to={`/staff/${slug}/forgot-password`}
              className="text-sm text-[#FDB913] hover:text-[#e5a811] font-medium"
            >
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                Sign In
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>

        {/* Powered by Dine AI */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Powered by{' '}
            <span className="font-medium">
              Dine <span className="text-[#FDB913]">AI</span>
            </span>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VHStaffLoginBranded;
